import { useState, useEffect } from 'react';
import { Sparkles } from 'lucide-react';
import { shopAPI } from '@/api/shop';

/**
 * Reusable BudleeAIIcon component that displays the equipped character from shop inventory.
 * Falls back to default icon if no character is equipped or on error.
 * 
 * @param {Object} props
 * @param {string} props.size - Size class (default: 'w-20 h-20' for container, 'w-10 h-10' for icon)
 * @param {string} props.iconSize - Size class for default icon (default: 'w-10 h-10')
 * @param {string} props.position - Position classes (default: 'fixed bottom-8 right-8')
 * @param {Function} props.onClick - Click handler
 * @param {boolean} props.showBadge - Whether to show "Hi!" badge (default: true)
 * @param {string} props.className - Additional classes for the container
 */
export default function BudleeAIIcon({ 
  size = 'w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20', 
  iconSize = 'w-7 h-7 sm:w-8 h-8 lg:w-10 h-10',
  position = 'fixed bottom-6 sm:bottom-7 right-4 sm:right-6',
  onClick,
  showBadge = true,
  className = ''
}) {
  const [equippedCharacter, setEquippedCharacter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    const loadEquippedCharacter = async () => {
      try {
        const inventoryResponse = await shopAPI.getInventory();
        const inventoryItems = Array.isArray(inventoryResponse) 
          ? inventoryResponse 
          : (inventoryResponse.results || inventoryResponse);
        
        // Find equipped character
        const character = inventoryItems.find(
          inv => inv.is_equipped && inv.shop_item?.item_type === 'CHARACTER'
        );
        
        setEquippedCharacter(character?.shop_item || null);
      } catch (err) {
        // Silently fail - use default icon
        console.warn('Could not load equipped character:', err);
        setEquippedCharacter(null);
      } finally {
        setLoading(false);
      }
    };

    loadEquippedCharacter();
  }, []);

  const containerClasses = `${position} z-50 ${onClick ? 'cursor-pointer' : ''} ${className}`;
  const showImage = !loading && equippedCharacter?.image_url_display && !imageError;

  return (
    <div className={containerClasses} onClick={onClick}>
      <div className="relative animate-float hover:scale-110 transition-transform duration-300">
        <div className={`${size} bg-gradient-to-br from-[hsl(var(--green-bright))] to-[hsl(var(--accent))] rounded-full flex items-center justify-center shadow-2xl glow-green overflow-hidden`}>
          {showImage ? (
            <img 
              src={equippedCharacter.image_url_display} 
              alt={equippedCharacter.item_name || 'Budlee AI Character'}
              className="w-full h-full object-contain"
              onError={() => {
                // Fallback to default icon on image load error
                setImageError(true);
              }}
            />
          ) : (
            <Sparkles className={`${iconSize} text-[hsl(var(--main-bg,200_18%_11%))]`} />
          )}
        </div>
        {showBadge && (
          <div className="absolute -top-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 bg-[hsl(var(--orange-warm))] rounded-full flex items-center justify-center shadow-lg">
            <span className="text-[hsl(var(--main-bg))] text-[9px] sm:text-[10px] font-bold">Hi!</span>
          </div>
        )}
      </div>
    </div>
  );
}
